import AppModule from '@app/lib/AppModule';
import { tasksState } from './state';
import { CreateTaskOptions, TaskState } from './types';
import Task from './Task';
import appManager from '@app/appManager';
import ToastModule from '@modules/ToastModule';

// THIS MODULE IS A WORK IN-PROGRESS A WILL BE REFACTORED
// SEE DAVEW BEFORE MAKING MAJOR CHANGES

function getTaskEvents(): string[] {
  return [
    'task.offered',
    'task.rejected',
    'task.abandoned',
    'task.accepted',
    'task.wrapup',
    'task.completed',
    'task.updated',
    'task.action.accept',
    'task.action.reject',
    'task.action.complete',
  ];
}

export class TasksModule extends AppModule {
  override events = [...getTaskEvents()];

  public createTask(options: CreateTaskOptions): Task {
    this.app.logger.info('tasks:create', options);
    const newTask = new Task(options);
    tasksState.tasks[newTask.id] = newTask;
    return tasksState.tasks[newTask.id];
  }

  /**
   * In order for us to populate the task with tenantId and the customer name, we need to create one before we place the call.
   * Once the outbound call is made, we will try to find the task with the same number and update the task with the sessionId.
   * Handled in `setOutboundCallToOffered`.
   */
  public createOutboundCallTask(
    outgoingNumber: string,
    displayName?: string,
    tenantId?: string,
  ) {
    return this.createTask({
      isInternal: true,
      id: `tmp-outbound-${outgoingNumber}`,
      from: outgoingNumber,
      displayName,
      direction: 'outbound',
      type: 'voice',
      state: TaskState.Pending,
      tenantId,
    });
  }

  // This method is used to update the task id when the outbound call is made. Since we create it with
  // a temporary session id (`tmp-outbound`), we need to update it with the actual session id when the
  // call is placed.
  public updateTaskId(task: Task, newSessionId: string) {
    tasksState.tasks[newSessionId] = task;
    this.deleteTask(task.id);
    tasksState.tasks[newSessionId].id = newSessionId;
  }

  public setTaskState(sessionId: string, taskState: TaskState) {
    const task = tasksState.tasks[sessionId];
    if (!task) {
      this.app.logger.error('task not found', sessionId);
      return;
    }
    task.state = taskState;
  }

  public setTaskAttributes(taskId: string, attributes: Record<string, any>) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.attributes = attributes;
  }

  public setTaskAttribute(taskId: string, key: string, value: any) {
    const task = tasksState.tasks[taskId];
    this.setTaskAttributes(taskId, {
      ...task.attributes,
      [key]: value,
    });
  }

  public setTaskDisplayName(taskId: string, displayName: string | undefined) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.displayName = displayName;
  }

  public setTaskComplete(taskId: string) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      console.error('task not found', taskId);
      return;
    }
    task.state = TaskState.Completed;
    this.deleteTask(taskId);
  }

  public deleteTask(taskId: string) {
    const task = tasksState.tasks[taskId];
    delete tasksState.tasks[taskId];

    ToastModule.hideIncomingCallToast(task.id);
  }

  public actionTask(taskId: string, action: 'accept' | 'reject' | 'complete') {
    const task = tasksState.tasks[taskId];
    if (!task) {
      console.error('task not found', taskId);
      return;
    }
    this.app.events.emit(`task.action.${action}`, task);
  }

  public getTask(taskId: string): Task | null {
    return tasksState.tasks[taskId] || null;
  }

  public getTasks(): Task[] {
    return Object.values(tasksState.tasks);
  }

  // This method is used to find the outbound call task by the from number.
  public findOutboundCallTask(outgoingNumber: string): Task | null {
    return (
      Object.values(tasksState.tasks).find(
        (task) =>
          task.state === TaskState.Pending &&
          task.type === 'voice' &&
          task.direction === 'outbound' &&
          task.from === outgoingNumber,
      ) || null
    );
  }

  // Find task by pcsConversationId
  public findTaskByPcsConversationSid(
    pcsConversationSid: string,
  ): Task | undefined {
    return Object.values(tasksState.tasks).find(
      (task) => task.id === pcsConversationSid,
    );
  }
}

export default new TasksModule(appManager.events, appManager.logger('Tasks'));
